import type { ModuleDef } from './types.js';

/** People: who works here, their records, and how they are onboarded and exited. */
export const PEOPLE_MODULES: readonly ModuleDef[] = [
  {
    key: 'employees',
    label: 'Employees',
    group: 'people',
    permissions: [
      { action: 'view', defaultRoles: ['HR', 'MANAGER'], description: 'Open employee records inside your scope' },
      { action: 'create', defaultRoles: ['HR'], description: 'Add an employee' },
      { action: 'edit', defaultRoles: ['HR'], description: 'Change an employee record' },
      { action: 'delete', defaultRoles: [], description: 'Remove an employee' },
      { action: 'export', defaultRoles: ['HR'], description: 'Download the employee list' },
      { action: 'manage', defaultRoles: [], description: 'Grant sign-in access and set access scope' },
    ],
  },
  {
    key: 'employees.import',
    label: 'Employee list import',
    group: 'people',
    permissions: [
      { action: 'create', defaultRoles: [], description: 'Import a spreadsheet of employees' },
    ],
  },
  {
    key: 'employees.site_workers',
    label: 'Site worker headcount',
    group: 'people',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'See how many site workers are on the roster' },
    ],
  },
  {
    key: 'employees.login',
    label: 'Employee sign-in accounts',
    group: 'people',
    permissions: [
      { action: 'create', defaultRoles: ['HR'], description: 'Create a sign-in account for an existing employee' },
      { action: 'manage', defaultRoles: ['HR'], description: 'Reset an employee’s password, or email them a reset link' },
    ],
  },
  {
    key: 'employees.history',
    label: 'Employment history',
    group: 'people',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'Read the employment history of any employee' },
      { action: 'create', defaultRoles: ['HR'], description: 'Add an entry to the employment history of any employee' },
    ],
  },
  {
    key: 'employees.documents',
    label: 'Employee documents',
    group: 'people',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'Open the documents of any employee, not only your own team' },
      { action: 'create', defaultRoles: ['HR'], description: 'Upload a document to any employee file' },
      { action: 'delete', defaultRoles: ['HR'], description: 'Remove an employee document' },
    ],
  },
  {
    key: 'employees.bulk',
    label: 'Bulk employee changes',
    group: 'people',
    permissions: [
      { action: 'edit', defaultRoles: ['HR'], description: 'Activate, deactivate or reassign many employees at once' },
    ],
  },
  {
    key: 'onboarding',
    label: 'Onboarding',
    group: 'people',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'Open the onboarding checklist of any employee' },
      { action: 'create', defaultRoles: ['HR'], description: 'Start onboarding for an employee' },
      { action: 'edit', defaultRoles: ['HR'], description: 'Tick off onboarding checklist items' },
      { action: 'approve', defaultRoles: ['HR'], description: 'Mark an onboarding as complete' },
    ],
  },
  {
    key: 'offboarding',
    label: 'Offboarding',
    group: 'people',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'Open the offboarding record of any employee' },
      { action: 'create', defaultRoles: ['HR'], description: 'Start offboarding for an employee' },
      { action: 'edit', defaultRoles: ['HR'], description: 'Update offboarding steps and asset returns' },
      { action: 'approve', defaultRoles: ['HR'], description: 'Finalise the exit of an employee' },
    ],
  },
  {
    key: 'letters',
    label: 'Employment letters',
    group: 'people',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'See the letter requests of every employee' },
      { action: 'create', defaultRoles: ['HR'], description: 'Request a letter on behalf of another employee, approved immediately' },
      { action: 'approve', defaultRoles: ['HR', 'MANAGER'], description: 'Approve or refuse letter requests' },
    ],
  },
  {
    key: 'tasks',
    label: 'Staff tasks',
    group: 'people',
    permissions: [
      { action: 'view', defaultRoles: ['HR', 'MANAGER'], description: 'See the tasks of everyone in your scope' },
      { action: 'create', defaultRoles: ['HR', 'MANAGER'], description: 'Give a task to employees in your scope' },
      { action: 'edit', defaultRoles: ['HR', 'MANAGER'], description: 'Change, remind about or cancel a task you can see' },
      { action: 'approve', defaultRoles: ['HR', 'MANAGER'], description: 'Approve a handed-in task or send it back' },
    ],
  },
];
